import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";

/* eslint-disable react/jsx-no-undef */
import { useState, useRef, useEffect } from 'react';
import UIAccordionItem from 'UIComponents/accordion/UIAccordionItem';
import Small from 'UIComponents/elements/Small';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIStepIndicator from 'UIComponents/connectedStep/UIStepIndicator'; // @ts-expect-error Could not find a declaration file for module

import ContentItemContainer from '../../../containers/Navigator/LessonNavigator/ContentItemContainer';
import ContentStepIconIndicator from './ContentStepIconIndicator'; // @ts-expect-error Could not find a declaration file for module

import LessonDuration from '../../LessonPage/LessonDuration'; // @ts-expect-error Could not find a declaration file for module

import ExpandedTrackNavigatorTopLevel from '../TrackNavigator/ExpandedTrackNavigatorTopLevel';
import { FormattedMessage } from 'LearningCenterUI/components/I18n';
import styled from 'styled-components';
import { OLAF, GYPSUM, BATTLESHIP, SLINKY } from 'HubStyleTokens/colors';
import { useLessonProgressById } from '../../../features/userProgress';
import { safeSmoothScrollTo } from '../../../utils/utils';
import { CardMenuIcon } from '../../../features/cardMenu';
import UIBox from 'UIComponents/layout/UIBox';
import { HasParentPlaylist, NextPlaylistContent } from '../../../features/playlistContent';
const BASE_CLASS_NAME = 'expanded-lesson-navigator-lesson';
const CompletedIconMessage = styled(FormattedMessage).withConfig({
  displayName: "ExpandedLessonNavigatorLesson__CompletedIconMessage",
  componentId: "sc-1wpn6fm-0"
})(["margin-left:2px;font-weight:500;color:", " !important;"], SLINKY);

const useAutoScrollToSelectedContent = (lessonRef, contentRef, currentIndex) => {
  useEffect(() => {
    if (currentIndex < 0) return;
    const selectedContent = contentRef.current && contentRef.current.children[0] && contentRef.current.children[0].children[currentIndex];
    const sidebarNavigator = document.getElementById('sidebarNavigator');
    if (!sidebarNavigator || !lessonRef.current || !selectedContent) return;
    const {
      offsetTop: selectedContentPosition,
      offsetHeight: selectedContentHeight
    } = selectedContent;
    const {
      offsetHeight: scrollHeight,
      scrollTop: currentScroll
    } = sidebarNavigator;
    const {
      offsetTop: lessonPosition
    } = lessonRef.current; // don't scroll if it is visible

    if (selectedContentPosition <= currentScroll + scrollHeight && selectedContentPosition >= currentScroll) return; // either scroll to the top lesson or to the selected content if it is not visible

    const scrollPosition = lessonPosition + scrollHeight > selectedContentPosition ? lessonPosition : selectedContentPosition - scrollHeight + selectedContentHeight;
    safeSmoothScrollTo(sidebarNavigator, 0, scrollPosition);
  }, [lessonRef, currentIndex, contentRef]);
};

const LessonNavigatorLesson = ({
  content,
  currentContentId,
  includeCompleteIcon,
  lesson,
  urlPrefix
}) => {
  const [userSelectedOpen, setUserSelectedOpen] = useState(false);
  const [userHasTakenControlOfOpenState, setUserHasTakenControlOfOpenState] = useState(false);
  const lessonRef = useRef(null);
  const contentRef = useRef(null);
  const lessonId = Number(lesson.get('id'));
  const lessonLanguage = lesson.get('language');
  const {
    lessonProgressById
  } = useLessonProgressById(lessonId);

  const handleOpenClick = event => {
    setUserSelectedOpen(event.target.value);
    setUserHasTakenControlOfOpenState(true);
  };

  const renderStepIconComponent = ({
    stepIndex,
    index,
    circleSize
  }) => {
    const thisContent = content.get(index);

    if (!thisContent) {
      return /*#__PURE__*/_jsx(ContentStepIconIndicator, {
        circleSize: circleSize,
        isActive: false
      });
    }

    const isActive = index === stepIndex;
    const contentId = thisContent.get('id');
    return /*#__PURE__*/_jsx(ContentStepIconIndicator, {
      circleSize: circleSize,
      contentId: contentId,
      isActive: isActive
    });
  };

  const currentIndex = lesson.get('contentIds').indexOf(currentContentId);
  const isSelected = lesson.get('contentIds').contains(currentContentId);
  const allComplete = lessonProgressById && lesson.get('contentIds').length === lessonProgressById ? lessonProgressById.contentProgress.length : 0;
  const isOpen = userHasTakenControlOfOpenState ? userSelectedOpen : isSelected;
  useAutoScrollToSelectedContent(lessonRef, contentRef, currentIndex);
  return /*#__PURE__*/_jsxs("div", {
    ref: lessonRef,
    children: [/*#__PURE__*/_jsx(UIAccordionItem, {
      className: `${BASE_CLASS_NAME} ${BASE_CLASS_NAME}--${isSelected ? 'selected' : 'unselected'}`,
      contentClassName: "lesson-navigator-lesson__content",
      "data-test-id": "lesson-navigator-lesson-UIAccordionItem",
      onOpenChange: handleOpenClick,
      open: isOpen,
      size: "medium",
      style: {
        backgroundColor: `${isSelected ? GYPSUM : OLAF}`,
        borderColor: `${isSelected && BATTLESHIP}`
      },
      title: /*#__PURE__*/_jsx("div", {
        "data-test-id": "lesson-navigator-lesson",
        children: /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsxs(UIFlex, {
            className: `${BASE_CLASS_NAME}__title`,
            children: [lesson.get('name'), /*#__PURE__*/_jsx(UIBox, {
              style: {
                position: 'absolute',
                right: -24
              },
              children: /*#__PURE__*/_jsx(CardMenuIcon, {
                language: lessonLanguage,
                lessonId: lessonId
              })
            })]
          }), includeCompleteIcon && allComplete ? /*#__PURE__*/_jsx(Small, {
            children: /*#__PURE__*/_jsx(CompletedIconMessage, {
              message: "learningCenter.tracks.recommendations.completed"
            })
          }) : /*#__PURE__*/_jsx(Small, {
            className: `${BASE_CLASS_NAME}__info`,
            use: "help",
            children: /*#__PURE__*/_jsx(LessonDuration, {
              contentList: content
            })
          })]
        })
      }),
      children: /*#__PURE__*/_jsx(UIFlex, {
        className: "m-top-4",
        "data-test-id": "lesson-navigator-lesson-UIStepIndicator",
        ref: contentRef,
        children: /*#__PURE__*/_jsx(UIStepIndicator, {
          StepIconComponent: renderStepIconComponent,
          circleSize: 10,
          className: `${BASE_CLASS_NAME}__step-indicator`,
          direction: "vertical",
          stepIndex: currentIndex,
          stepNames: content.map(thisContent => {
            return /*#__PURE__*/_jsx("div", {
              className: thisContent.get('id') === currentContentId ? `${BASE_CLASS_NAME}__select` : '',
              style: {
                width: '300%',
                marginLeft: '-100px'
              },
              children: /*#__PURE__*/_jsx("div", {
                style: {
                  width: '33%',
                  marginLeft: '100px'
                },
                children: /*#__PURE__*/_jsx(ContentItemContainer, {
                  id: thisContent && thisContent.get('id'),
                  parentId: lesson.get('id'),
                  urlPrefix: urlPrefix
                }, thisContent && thisContent.get('id'))
              })
            }, thisContent && thisContent.get('id'));
          }).toArray()
        })
      })
    }), urlPrefix === 'lessons' && /*#__PURE__*/_jsx(HasParentPlaylist, {
      fallback: /*#__PURE__*/_jsx(ExpandedTrackNavigatorTopLevel, {
        "data-test-id": "expanded-track-navigator-lesson-recommendations",
        heading: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.tracks.recommendations.navigation.heading"
        }),
        isSelected: currentContentId === `recommendations`,
        subheading: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.tracks.recommendations.navigation.subheading"
        }),
        url: `recommendations`
      }),
      children: /*#__PURE__*/_jsx(NextPlaylistContent, {
        lessonId: lesson.get('id') && Number(lesson.get('id')),
        render: ({
          title,
          subtitle,
          url,
          urlFragments
        }) => /*#__PURE__*/_jsx(ExpandedTrackNavigatorTopLevel, {
          "data-test-id": "expanded-track-navigator-next-playlist-content",
          heading: title,
          isSelected: urlFragments.includes(currentContentId),
          subheading: subtitle,
          url: url
        })
      })
    })]
  });
};

export default LessonNavigatorLesson;